import { isAxiosError } from "axios";

// Excel endpoints use responseType: "arraybuffer", so error bodies (plain
// strings from the backend, JSON-encoded or not) arrive as raw bytes instead
// of being parsed by axios automatically.
export const extractArrayBufferErrorMessage = (
    error: unknown
): string | undefined => {
    if (!isAxiosError(error) || !(error.response?.data instanceof ArrayBuffer)) {
        return undefined;
    }

    const text = new TextDecoder().decode(error.response.data).trim();
    if (!text) {
        return undefined;
    }

    try {
        const parsed = JSON.parse(text);
        if (typeof parsed === "string") {
            return parsed;
        }
    } catch {
        // Backend returned text/plain rather than a JSON-encoded string.
    }

    return text;
};

// Some excel endpoints return a non-2xx status but still carry a structured
// JSON body (e.g. a summary with errors/warnings) instead of a plain message.
// Since responseType is "arraybuffer", that body needs to be decoded manually
// before it can be handled like a normal success response.
export const extractArrayBufferErrorJson = (
    error: unknown
): unknown | undefined => {
    if (!isAxiosError(error) || !(error.response?.data instanceof ArrayBuffer)) {
        return undefined;
    }

    const text = new TextDecoder().decode(error.response.data).trim();
    if (!text) {
        return undefined;
    }

    try {
        const parsed = JSON.parse(text);
        if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
            return parsed;
        }
    } catch {
        // Not JSON, caller falls back to plain text handling.
    }

    return undefined;
};

// Excel upload endpoints that return a prefilled binary excel file report
// warnings/errors via X-Sumi-Warnings/X-Sumi-Errors headers (JSON-encoded
// string arrays), since the response body itself can't carry JSON.
export const extractHeaderStringArray = (
    headers: Record<string, unknown> | undefined,
    headerName: string
): string[] | undefined => {
    const value = headers?.[headerName];
    if (typeof value !== "string" || !value.trim()) {
        return undefined;
    }

    try {
        const parsed = JSON.parse(value);
        if (Array.isArray(parsed)) {
            return parsed.map(String);
        }
    } catch {
        // Not JSON-encoded, treat the whole header value as a single message.
    }

    return [value];
};
